import {userAIView} from "./triproutes-user-ai.mjs";
export const ACTIVE_RUN_STATES = new Set(["queued", "running", "suspended"]);
export const TERMINAL_RUN_STATES = new Set(["completed", "failed", "cancelled", "timed_out"]);

export function normalizeApiBase(value = "") {
  const base = String(value || "").trim();
  if (!base || base === "/") return "";
  return base.replace(/\/+$/, "");
}

export function apiUrl(base, path) {
  const normalized = normalizeApiBase(base);
  const endpoint = `/${String(path || "").replace(/^\/+/, "")}`;
  if (/\/v1$/i.test(normalized) && endpoint.startsWith("/v1/")) {
    return `${normalized}${endpoint.slice(3)}`;
  }
  return `${normalized}${endpoint}`;
}

export function requestHeaders(credentials = {}, extra = {}) {
  const headers = { Accept: "application/json", ...extra };
  const values = {
    Authorization: credentials.token ? `Bearer ${credentials.token}` : "",
    "X-AI-Organization-ID": credentials.organizationId,
    "X-AI-Project-ID": credentials.projectId,
    "X-AI-Environment-ID": credentials.environmentId,
  };
  for (const [name, value] of Object.entries(values)) {
    if (String(value || "").trim()) headers[name] = String(value).trim();
  }
  return headers;
}

export function scopeCredentials(session) {
  const scope = session?.scope;
  return {
    organizationId: scope?.organizationId,
    projectId: scope?.projectId,
    environmentId: scope?.environmentId,
  };
}

export function credentialsComplete(credentials = {}) {
  return ["token", "organizationId", "projectId", "environmentId"].every((key) =>
    Boolean(String(credentials[key] || "").trim()),
  );
}

export function cookieValue(cookieHeader = "", name = "") {
  const prefix = `${encodeURIComponent(name)}=`;
  for (const part of String(cookieHeader || "").split(";")) {
    const value = part.trim();
    if (value.startsWith(prefix)) return decodeURIComponent(value.slice(prefix.length));
  }
  return "";
}

export function isPendingResultError(error = {}) {
  return Number(error?.status) === 404 || ["run_result_not_ready", "task_result_not_ready", "task_result_absent", "task_result_missing"].includes(error?.code);
}

export function getByJsonPointer(value, pointer) {
  if (pointer === "") return value;
  if (typeof pointer !== "string" || !pointer.startsWith("/")) return undefined;
  return pointer
    .slice(1)
    .split("/")
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .reduce((current, key) => {
      if (current == null || !Object.prototype.hasOwnProperty.call(Object(current), key)) return undefined;
      return current[key];
    }, value);
}

export function setByPointer(target, pointer, value) {
  if (!pointer || pointer === "/") return target;
  const parts = pointer.replace(/^\//, "").split("/").filter(Boolean);
  let cursor = target;
  parts.forEach((part, index) => {
    const key = part.replace(/~1/g, "/").replace(/~0/g, "~");
    if (index === parts.length - 1) cursor[key] = value;
    else cursor = cursor[key] ||= {};
  });
  return target;
}

export function durationLabel(milliseconds) {
  if (milliseconds == null || Number.isNaN(Number(milliseconds))) return "—";
  const value = Math.max(0, Number(milliseconds));
  if (value < 1000) return `${Math.round(value)} мс`;
  if (value < 60_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} с`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.round((value % 60_000) / 1000);
  return `${minutes} мин ${seconds} с`;
}

export function moneyLabel(amount, currency = "") {
  if (amount == null || amount === "") return "Нет оценки";
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount}${currency ? ` ${currency}` : ""}`;
  if (value !== 0 && Math.abs(value) < 0.0000005) return `< 0,000001${currency ? ` ${currency}` : ""}`;
  const digits = Math.abs(value) < 0.01 && value !== 0 ? 6 : 2;
  return `${value.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits })}${currency ? ` ${currency}` : ""}`;
}

// The financial detail view keeps the stored decimal precision. JavaScript
// numbers are used only for the compact headline above it.
export function exactMoneyLabel(amount, currency = "") {
  if (amount == null || amount === "") return "Нет сведений";
  const raw = String(amount).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return `${raw}${currency ? ` ${currency}` : ""}`;
  const [integer, fraction] = raw.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  return `${grouped}${fraction == null ? "" : `,${fraction}`}${currency ? ` ${currency}` : ""}`;
}

export function buildRefKey(ref = {}) {
  return ref?.id && ref?.version ? `${ref.id}@${ref.version}${ref.digest ? `#${ref.digest}` : ""}` : "";
}

export function budgetValidationError(raw, maximum) {
  const value = String(raw ?? "").trim();
  if (!/^\d+(?:\.\d{1,9})?$/.test(value) || !/[1-9]/.test(value)) return "Укажите положительный лимит расходов";
  if (maximum != null && maximum !== "") {
    const limit = String(maximum).trim();
    if (/^\d+(?:\.\d{1,9})?$/.test(limit)) {
      const scale = Math.max((value.split(".")[1] || "").length, (limit.split(".")[1] || "").length);
      const scaled = (text) => {
        const [whole, fraction = ""] = text.split(".");
        return BigInt(whole + fraction.padEnd(scale, "0"));
      };
      if (scaled(value) > scaled(limit)) return `Лимит организации: ${exactMoneyLabel(limit)}`;
    }
  }
  return "";
}

export function quoteIsCurrent(quote, now = Date.now()) {
  if (!quote?.quoteRef?.id) return false;
  const expiresAt = quote.quoteRef.expiresAt || quote.expiresAt;
  const expiry = Date.parse(expiresAt || "");
  return Number.isFinite(expiry) && expiry > now;
}

export function usageQuantity(attempt = {}, metric) {
  const measurements = attempt.actualUsage;
  if (!Array.isArray(measurements) || measurements.length === 0) return null;
  const aliases = metric === "cache_write_tokens" ? ["cache_write_tokens", "cached_write_tokens"]
    : metric === "cached_read_tokens" ? ["cached_read_tokens", "cached_input_tokens"] : [metric];
  const selected = measurements.filter((item) => aliases.includes(item.metric) && item.unit === "token");
  if (!selected.length || selected.some((item) => item.quantity == null || item.quantity === "" || !Number.isFinite(Number(item.quantity)))) return null;
  return selected.reduce((sum, item) => sum + Number(item.quantity), 0);
}

export function billableTokenTotal(attempt = {}) {
  const input = usageQuantity(attempt, "input_tokens");
  const output = usageQuantity(attempt, "output_tokens");
  return input == null || output == null ? null : input + output;
}

export function financeStatusLabel(status) {
  return ({ preliminary: "Предварительная оценка", final: "Рассчитано по тарифу модели",
    calculated: "Рассчитано по тарифу модели", partial: "Учтено частично",
    reconciled: "Сверено с провайдером", charged: "Начислено", paid: "Списано",
    not_recorded: "Запись о начислении отсутствует", unavailable: "Нет сведений", unknown: "Нет сведений" })[status] || "Нет сведений";
}

export function financeAttemptReasonLabel(code = "") {
  return ({
    invalid_model_output: "Ответ модели не прошёл проверку",
    invalid_evidence_reference: "Не удалось подтвердить ссылку на источник",
    usage_ledger_incomplete: "Данные об использовании модели неполные",
    task_budget_exceeded: "Достигнут порог расходов задачи",
    organization_budget_exceeded: "Недостаточно доступного баланса организации",
    task_budget_unavailable: "Порог расходов не удалось проверить",
    lease_expired: "Превышено время выполнения этапа",
    stage_execution_failed: "Этап завершился с ошибкой",
    provider_call_failed: "Вызов провайдера завершился с ошибкой",
    provider_outcome_unconfirmed: "Исход вызова провайдера не подтверждён",
    provider_outcome_unresolved: "Повтор остановлен: предыдущий вызов требует проверки",
    artifact_store_failed: "Не удалось сохранить результат; восстанавливаем сохранение",
    stage_checkpoint_write_failed: "Не удалось сохранить промежуточный результат",
    stage_checkpoint_invalid: "Сохранённый результат не прошёл проверку целостности",
  })[code] || (code ? "Причина не уточнена" : "");
}

// A customer charge calculation is distinct from a posted charge and a
// payment. BYOK provider costs never become a platform token charge here.
export function financeChargeView(charge = {}, billingMode = "") {
  const byok = billingMode === "CUSTOMER_BYOK";
  const notApplicable = byok || charge?.status === "not_applicable";
  const calculated = !notApplicable && charge?.status === "calculated" &&
    charge.amount != null && charge.amount !== "" && Boolean(charge.currency);
  return {
    byok,
    calculated,
    title: byok ? "Начисление платформы за вызовы" : "Расчёт к начислению потребителю",
    value: notApplicable ? "Не применяется" : calculated
      ? exactMoneyLabel(charge.amount, charge.currency) : "Расчёт недоступен",
    posting: charge?.postingStatus === "not_recorded" ? "запись отсутствует"
      : ["recorded", "charged"].includes(charge?.postingStatus) ? "зарегистрировано" : "нет сведений",
    payment: charge?.paymentStatus === "not_recorded" ? "запись отсутствует"
      : charge?.paymentStatus === "paid" ? "оплачено"
      : charge?.paymentStatus === "recorded" ? "зарегистрировано" : "нет сведений",
  };
}

export function runCost(run = {}, usage = {}) {
  const coverage = run.costCoverage || {};
  return {
    amount: usage.providerCost ?? coverage.providerCost,
    currency: usage.currency || coverage.currency || "",
    coverage: usage.coverage || coverageLabel(coverage),
  };
}

export function costPresentation(cost = {}, active = false) {
  const coverage = cost.coverage || "none";
  if (coverage === "mixedCurrencies") return { value: "Нет общей суммы", note: "Расходы в разных валютах · только расходы Harness" };
  const known = cost.amount != null && cost.amount !== "";
  if (!known) return { value: "Нет данных", note: "Расходы на модели пока неизвестны" };
  const amount = moneyLabel(cost.amount, cost.currency);
  if (cost.status === "partial" || coverage === "partial") return { value: amount, note: "Учтено частично · только расходы Harness" };
  if (cost.status === "reconciled") return { value: amount, note: "Сверено с провайдером · только расходы Harness" };
  if (["final", "calculated"].includes(cost.status)) return { value: amount, note: "Рассчитано по тарифу модели · только расходы Harness" };
  if (cost.status === "preliminary" || active || coverage !== "complete") return { value: amount, note: "Предварительно · только расходы Harness" };
  return { value: amount, note: "Рассчитано по тарифу модели · только расходы Harness" };
}

export function effectiveStageState(stage = {}, runState = "", dependencies = []) {
  if (!stage.state) return { state: "unknown", label: "Состояние неизвестно" };
  const attempts = stage.attempts || [];
  if (TERMINAL_RUN_STATES.has(runState) && stage.state === "queued" && !attempts.length && !Number(stage.attemptCount || 0)) {
    return { state: "not_started", label: "Не запускалась — выполнение завершено" };
  }
  // A finished run never waits for anything. A branch left in retry_wait ended
  // without a result; calling it "awaiting retry" promises a retry that the
  // run will never make.
  if (TERMINAL_RUN_STATES.has(runState) && stage.state === "retry_wait") {
    return { state: "branch_ended", label: "Ветка завершена без результата" };
  }
  if (TERMINAL_RUN_STATES.has(runState) && stage.state === "running" && !stage.completedAt) {
    return { state: "inconsistent", label: "Состояние стадии не согласовано с завершением запуска" };
  }
  if (ACTIVE_RUN_STATES.has(runState) && stage.state === "queued" && dependencies.some((item) => item.state !== "completed")) {
    return { state: "waiting", label: "Ожидает предыдущих этапов" };
  }
  if (stage.state === "skipped") return { state: "skipped", label: "Пропущена" };
  return { state: stage.state, label: "" };
}

// A failed model call names its own class. The operator reads it to decide what
// to fix: a limit, a binding, a provider outage, or nothing at all because the
// call never left us.
export const FAILURE_CLASS_LABELS = {
  local_precheck_failed: "Запрос не отправлен: проверка до вызова",
  provider_configuration_invalid: "Запрос не отправлен: настройка провайдера",
  provider_rejected: "Провайдер отклонил запрос",
  provider_unavailable: "Провайдер недоступен или ограничил запросы",
  provider_timeout: "Истекло ожидание ответа",
  invalid_model_output: "Ответ не прошёл проверку контракта",
  provider_outcome_unknown: "Исход вызова не подтверждён",
};

export function failureClassLabel(failureClass = "") {
  return FAILURE_CLASS_LABELS[failureClass] || failureClass || "Класс не записан";
}

// The stage that actually stopped the run is the one that failed, not the first
// one in the list that happens to carry an error code: a branch left waiting
// after the run ended still holds one.
export function failedStageOf(stages = []) {
  const moment = (stage) => {
    const times = [stage.completedAt, ...(stage.attempts || []).map((attempt) => attempt.completedAt || attempt.startedAt)]
      .filter(Boolean).map((value) => new Date(value).getTime()).filter((value) => !Number.isNaN(value));
    return times.length ? Math.max(...times) : 0;
  };
  const failed = stages.filter((stage) => stage.state === "failed" || (stage.attempts || []).some((attempt) => attempt.state === "failed"));
  if (failed.length) return failed.slice().sort((a, b) => moment(b) - moment(a))[0];
  return stages.find((stage) => stage.lastErrorCode || (stage.attempts || []).some((attempt) => attempt.errorCode));
}

export function orderStagesByDependencies(stages = [], manifestStages = []) {
  if (!Array.isArray(manifestStages) || !manifestStages.length) return { items: stages, known: false, levels: {} };
  const manifest = new Map(manifestStages.map((item) => [item.id || item.stageId, item]));
  const byId = new Map(stages.map((item) => [item.stageId, item]));
  if (stages.some((item) => !manifest.has(item.stageId))) return { items: stages, known: false, levels: {} };
  const sorted = [];
  const levels = {};
  const visiting = new Set();
  const visited = new Set();
  function visit(id) {
    if (visiting.has(id)) return false;
    if (visited.has(id)) return true;
    const node = manifest.get(id);
    if (!node) return false;
    visiting.add(id);
    let level = 0;
    for (const dep of node.dependsOn || []) {
      if (!byId.has(dep) || !visit(dep)) return false;
      level = Math.max(level, levels[dep] + 1);
    }
    visiting.delete(id);
    visited.add(id);
    levels[id] = level;
    sorted.push(byId.get(id));
    return true;
  }
  if (!stages.every((item) => visit(item.stageId))) return { items: stages, known: false, levels: {} };
  const manifestOrder = new Map(manifestStages.map((item, index) => [item.id || item.stageId, index]));
  sorted.sort((left, right) => levels[left.stageId] - levels[right.stageId] || manifestOrder.get(left.stageId) - manifestOrder.get(right.stageId));
  return { items: sorted, known: true, levels };
}

export function parseRoute(hash = "", pathname = "") {
  if (String(pathname || "").replace(/\/+$/, "") === "/console/admin/roadmap") {
    return { section: "admin", id: "roadmap", tab: "overview", taskId: "", source: "", list: "", legacyRunId: "" };
  }
  const raw = String(hash || "").replace(/^#/, "") || "/solutions";
  const [routePathname, query = ""] = raw.split("?");
  const pieces = routePathname.split("/").filter(Boolean);
  if (pieces[0] === "triproutes" && pieces[1] === "models") { pieces[0] = "settings"; pieces[1] = "models"; }
  const section = ["solutions", "agents", "tasks", "results", "metrics", "runs", "corplinks", "triproutes", "recovery", "settings", "workspace"].includes(pieces[0]) ? pieces[0] : "solutions";
  const params = new URLSearchParams(query);
  const legacyUserAI = section === "triproutes" && pieces[1] === "ai-panel";
  const userAI = section === "triproutes" && pieces[1] === "solutions" && pieces[2] === "user-ai";
  return { section, id: legacyUserAI ? "solutions" : pieces[1] ? decodeURIComponent(pieces[1]) : section === "corplinks" ? "agents" : section === "triproutes" ? "solutions" : "",
    ...(section === "solutions" && params.has("site") ? {site: params.get("site") === "app" ? "app" : "ru"} : {}),
    ...(["tasks","runs"].includes(section) && params.get("from") === "triproutes" && params.has("site") ? {site: params.get("site") === "app" ? "app" : "ru"} : {}),
    ...(legacyUserAI || userAI || section === "settings" && pieces[1] === "triproutes-solutions" && params.get("solution") === "user-ai" ? {userAISettingsParams:Object.fromEntries(params)} : {}),
    ...(section === "triproutes" ? { solution: legacyUserAI ? "user-ai" : pieces[1] === "solutions" && pieces[2] ? decodeURIComponent(pieces[2]) : "", site: new URLSearchParams(query).get("site") === "app" ? "app" : "ru" } : {}),
    ...(legacyUserAI ? {legacyUserAI:true} : {}),
    ...(section === "triproutes" && pieces[1] === "agents" ? { agentId: pieces[2] ? decodeURIComponent(pieces[2]) : "" } : {}),
    ...(section === "runs" && params.get("from") === "triproutes" && params.get("solution") === "user-ai" ? {solution:"user-ai",site:params.get("site")==="app"?"app":"ru"} : {}),
    legacyRunId: section === "corplinks" && pieces[1] === "runs" && pieces[2] ? decodeURIComponent(pieces[2]) : "",
    tab: legacyUserAI || userAI ? userAIView(params.get("tab"),legacyUserAI) : new URLSearchParams(query).get("tab") || "stages", taskId: new URLSearchParams(query).get("task") || "",
    source: new URLSearchParams(query).get("from") || "", list: new URLSearchParams(query).get("list") || "",
    ...(["workspace", "triproutes", "corplinks"].includes(section) && params.has("resource") ? { resourceID: params.get("resource"), authoringKind: params.get("kind") === "agent" ? "agent" : "pipeline" } : {}),
    ...(section === "settings" && pieces[1] === "ready-solutions" ? { ...(params.has("resource") ? { resourceID: params.get("resource"), authoringKind: params.get("kind") === "agent" ? "agent" : "pipeline" } : {}), agentId: new URLSearchParams(query).get("agent") || "", solutionId: new URLSearchParams(query).get("solution") || "" } : {}),
    ...(section === "settings" && pieces[1] === "triproutes-solutions" ? { site: params.get("site") === "app" ? "app" : "ru", solutionId: params.get("solution") || "", solutionState:["archived","all"].includes(params.get("state")) ? params.get("state") : "active", ...(params.has("resource") ? {resourceID:params.get("resource"),authoringKind:params.get("kind") === "agent" ? "agent" : "pipeline"} : {}) } : {}),
    ...(section === "settings" && pieces[1] === "models" ? { site: params.get("site") === "app" ? "app" : "ru" } : {}),
    ...(section === "triproutes" && pieces[1] === "settings" ? { solutionId: params.get("solution") || "" } : {}),
    ...(section === "tasks" && new URLSearchParams(query).get("platform") === "1" ? { platformScope: {
      organizationId: new URLSearchParams(query).get("organizationId") || "",
      projectId: new URLSearchParams(query).get("projectId") || "",
      environmentId: new URLSearchParams(query).get("environmentId") || "",
    } } : {}) };
}

export function routeHash(section, id = "", query = {}) {
  const path = `#/${section}${id ? `/${encodeURIComponent(id)}` : ""}`;
  const params = new URLSearchParams(query);
  return params.size ? `${path}?${params}` : path;
}

export function validateSolutionInput(solution = {}, input = {}) {
  const errors = {};
  for (const field of solution.uiSchema?.fields || []) {
    const value = getByJsonPointer(input, field.path);
    const empty = value == null || value === "";
    if (field.required && empty) { errors[field.path] = "Заполните поле"; continue; }
    if (empty) continue;
    if (field.path.toLowerCase().endsWith("/inn") && !/^(\d{10}|\d{12})$/.test(String(value))) errors[field.path] = "Введите ИНН: 10 или 12 цифр";
    if (field.control === "select") {
      const options = field.options || [];
      if (!options.some((option) => String(option.value) === String(value))) errors[field.path] = "Выберите значение из списка";
    }
  }
  return errors;
}

export function coverageLabel(coverage = {}) {
  const attempts = Number(coverage.attemptCount || 0);
  const unknown = Number(coverage.unknownAttempts || 0) + Number(coverage.ambiguousAttempts || 0);
  if (!attempts) return "none";
  return unknown === 0 ? "complete" : unknown >= attempts ? "none" : "partial";
}

export function versionRefLabel(ref) {
  if (!ref) return "—";
  if (typeof ref === "string") return ref;
  const id = ref.id || ref.deploymentId || "—";
  if (ref.version) return `${id}@${ref.version}`;
  if (ref.revision != null) return `${id} · rev ${ref.revision}`;
  return id;
}

export function humanValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Да" : "Нет";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

export function severityLabel(severity) {
  return ({ info: "Справочно", low: "Низкая значимость", medium: "Требует внимания", warning: "Требует внимания",
    high: "Высокая значимость", critical: "Критично" })[severity] || "";
}

export function readableListItems(value = []) {
  if (!Array.isArray(value)) return [];
  const displayValue = (item) => {
    if (item == null || item === "") return "Нет данных";
    if (typeof item === "object") return Array.isArray(item) && item.every((part) => typeof part !== "object")
      ? item.map(humanValue).join(", ") : "Значение доступно в полной справке";
    return humanValue(item);
  };
  return value.map((item) => {
    if (item == null || typeof item !== "object") return { text: displayValue(item) };
    if (Array.isArray(item.items)) return {
      title: typeof item.title === "string" ? item.title : "Сведения",
      rows: item.items.filter((entry) => entry && typeof entry === "object" && (entry.label || entry.value != null))
        .map((entry) => ({ label: String(entry.label || "Показатель"), value: displayValue(entry.value), severity: severityLabel(entry.severity) })),
    };
    if (item.label || item.value != null) return { rows: [{ label: String(item.label || "Показатель"), value: displayValue(item.value), severity: severityLabel(item.severity) }] };
    const text = item.text || item.summary || item.description;
    return { text: typeof text === "string" ? text : "Сведения доступны в полной справке" };
  });
}

export function idempotencyKey(prefix = "ui") {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}
